import React, { useCallback, useEffect, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  House, ArrowsLeftRight, PaperPlaneTilt, HandCoins, User, ChatCircleText,
  UsersThree, ShieldCheck, ListChecks, SignOut, List, Coins, ChartPieSlice, BellRinging, Database
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { useBackgroundRefresh } from "../lib/refresh";
import { formatCLP } from "../lib/format";
import FloatingChat from "./FloatingChat";
import CMFFooter from "./CMFFooter";
import api from "../lib/api";
import InstallAppButton from "./InstallAppButton";
import OnboardingPrompts from "./OnboardingPrompts";

const navByRole = {
  client: [
    { to: "/app", label: "Inicio", icon: House, end: true },
    { to: "/app/movimientos", label: "Movimientos", icon: ListChecks },
    { to: "/app/transferencias", label: "Transferencias", icon: PaperPlaneTilt },
    { to: "/app/creditos", label: "Créditos", icon: HandCoins },
    { to: "/app/perfil", label: "Perfil", icon: User },
  ],
  advisor: [
    { to: "/app", label: "Panel", icon: ChartPieSlice, end: true },
    { to: "/app/clientes", label: "Clientes", icon: UsersThree },
    { to: "/app/solicitudes", label: "Solicitudes", icon: HandCoins },
    { to: "/app/autorizaciones", label: "Autorizaciones", icon: ShieldCheck, badgeKey: "pending" },
    { to: "/app/chat", label: "Chat", icon: ChatCircleText, badgeKey: "chat" },
    { to: "/app/perfil", label: "Perfil", icon: User },
  ],
  admin: [
    { to: "/app", label: "Overview", icon: ChartPieSlice, end: true },
    { to: "/app/usuarios", label: "Usuarios", icon: UsersThree },
    { to: "/app/creditos-admin", label: "Créditos", icon: Coins },
    { to: "/app/autorizaciones", label: "Autorizaciones", icon: ShieldCheck, badgeKey: "pending" },
    { to: "/app/auditoria", label: "Auditoría", icon: ListChecks },
    { to: "/app/respaldo", label: "Respaldo", icon: Database },
    { to: "/app/chat", label: "Chat", icon: ChatCircleText, badgeKey: "chat" },
    { to: "/app/perfil", label: "Perfil", icon: User },
  ],
};

export default function Layout() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [pendingTransfers, setPendingTransfers] = useState(0);

  const loadBadges = useCallback(async () => {
    if (!user || (user.role !== "advisor" && user.role !== "admin")) return;
    try {
      const [convs, pending] = await Promise.all([
        api.get("/chat/conversations"),
        api.get("/transfers/pending"),
      ]);
      setUnreadTotal(convs.data.reduce((s, c) => s + (c.unread || 0), 0));
      setPendingTransfers(pending.data.length);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    if (!user || (user.role !== "advisor" && user.role !== "admin")) return;
    loadBadges();
    const i = setInterval(loadBadges, 5000);
    return () => { clearInterval(i); };
  }, [user, loadBadges]);

  // Instant refresh on tab focus / push received — no waiting for the next 5s tick.
  useBackgroundRefresh(loadBadges);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-inkMuted font-mono text-xs uppercase tracking-widest">Cargando…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  const items = navByRole[user.role] || [];

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-bg text-ink">
      {/* Sidebar */}
      <aside className={`${mobileOpen ? "fixed inset-y-0 left-0 z-40" : "hidden"} md:flex md:static md:z-auto w-72 bg-surface border-r border-hairline flex-col`} data-testid="sidebar">
        <div className="px-6 py-7 border-b border-hairline">
          <Link to="/app" className="flex items-center gap-2">
            <img src="/logo-activa.png" alt="Activa Capital Privado" className="w-10 h-10 object-contain shrink-0" />
            <div>
              <div className="font-display font-black text-lg leading-none tracking-tight">Activa</div>
              <div className="label-mono mt-1">Capital Privado</div>
            </div>
          </Link>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-0.5">
          {items.map(({ to, label, icon: Icon, end, badgeKey }) => {
            const badgeCount = badgeKey === "chat" ? unreadTotal : badgeKey === "pending" ? pendingTransfers : 0;
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-${label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors border-l-2 ${
                    isActive ? "border-forest bg-forest/5 text-forest font-semibold" : "border-transparent text-ink hover:bg-bg"
                  }`
                }
              >
                <Icon size={18} weight="duotone" />
                <span className="font-body flex-1">{label}</span>
                {badgeCount > 0 && (
                  <span
                    data-testid={`nav-badge-${badgeKey}`}
                    className="bg-burnt text-bg text-[10px] font-mono font-bold px-1.5 py-0.5 leading-none min-w-[18px] text-center"
                  >
                    {badgeCount > 99 ? "99+" : badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>

        <div className="px-4 py-4 border-t border-hairline">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-forest text-bg flex items-center justify-center font-display font-bold">
              {user.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{user.name}</div>
              <div className="label-mono truncate">{user.role}</div>
            </div>
          </div>
          {user.role === "client" && (
            <div className="mb-3 px-3 py-2 bg-bg border border-hairline">
              <div className="label-mono">Saldo disponible</div>
              <div className="font-mono font-medium text-lg" data-testid="sidebar-balance">{formatCLP(user.balance)}</div>
            </div>
          )}
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="w-full h-10 flex items-center justify-center gap-2 text-sm border border-hairline hover:bg-forest hover:text-bg hover:border-forest transition-colors"
          >
            <SignOut size={16} weight="bold" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 bg-surface border-b border-hairline flex items-center justify-between px-4 md:px-8">
          <button
            className="md:hidden p-2 border border-hairline"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="mobile-menu-btn"
          >
            <List size={18} weight="bold" />
          </button>
          <div className="hidden md:flex items-center gap-6">
            <div className="label-mono">{new Date().toLocaleDateString("es-CL", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</div>
          </div>
          <div className="flex items-center gap-3">
            <InstallAppButton variant="ghost" />
            {(user.role === "advisor" || user.role === "admin") && (
              <Link
                to="/app/chat"
                className="relative p-2 border border-hairline hover:bg-bg transition-colors"
                data-testid="topbar-bell"
                title={unreadTotal > 0 ? `${unreadTotal} mensajes sin leer` : "Sin mensajes pendientes"}
              >
                <BellRinging size={18} weight={unreadTotal > 0 ? "fill" : "duotone"} className={unreadTotal > 0 ? "text-burnt" : "text-ink"} />
                {unreadTotal > 0 && (
                  <span
                    data-testid="topbar-bell-badge"
                    className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-burnt text-bg text-[10px] font-mono flex items-center justify-center"
                  >
                    {unreadTotal > 99 ? "99+" : unreadTotal}
                  </span>
                )}
              </Link>
            )}
            <span className="label-mono hidden sm:block">RUT · <span className="font-mono normal-case tracking-normal text-ink">{user.rut}</span></span>
            <div className="w-9 h-9 bg-burnt text-bg flex items-center justify-center font-display font-bold">
              {user.name?.[0]?.toUpperCase() || "U"}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <div className="p-6 md:p-8 lg:p-12 animate-fade-up">
            <Outlet />
          </div>
          {location.pathname !== "/app/chat" && <CMFFooter variant="compact" />}
        </main>
      </div>

      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Floating chat button — only for clients, hidden on the chat page itself */}
      {location.pathname !== "/app/chat" && <FloatingChat />}

      {/* Auto-prompts: PWA install + Push notifications (shown once per dismissal TTL) */}
      <OnboardingPrompts />
    </div>
  );
}
